import { PageService } from './../page.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap } from 'rxjs/operators';
import { urlConfig } from '../../../../config/url.config';
import { STEP_ID } from '../../enum/step-id.enum';
import { StorageModule } from '../../utils/storage.module';
import { ErrorResponse } from '../../model/error-response';
import { ProposalResponse } from '../../model/proposal-response';
import { IIdentifyResponse } from '../../model/identify-response';
import { EncryptionService } from '@afe/encryption';
import { LogRequest } from 'src/app/mfe-biometry/types/log-request';

@Injectable({
    providedIn: 'root',
})
export class RestService {

    private resource_url: string;
    private TRACKING_URL = urlConfig.url_traking;

    constructor(@Inject(HttpClient) public https: HttpClient,
        @Inject(StorageModule) public storage: StorageModule,
        @Inject(PageService) private pageService: PageService,
        @Inject(EncryptionService) private encryptionService: EncryptionService,
    ) {
        this.resource_url = urlConfig.url_api_bff;
    }

    public get<T>(path: string, authenticated: boolean = false, skipErrorRedirect: boolean = false): Observable<T> {
        let headers = new HttpHeaders();

        if (skipErrorRedirect) {
            headers = this.appendSkipErrorRedirectHeaders(headers);
        }

        if (authenticated) {
            headers = this.appendHeaders(headers);
        }

        let request = authenticated ? this.https.get<T>(`${this.resource_url}/${path}`, { headers: this.authHeaders }) : this.https.get<T>(`${this.resource_url}/${path}`);

        return request;
    }

    public post<T>(path: string, data: any, authenticated: boolean = false): Observable<T> {
        if (authenticated) {
            return this.https.post<T>(`${this.resource_url}/${path}`, data, { headers: this.authHeaders });
        }
        else {
            return this.https.post<T>(`${this.resource_url}/${path}`, data);
        }

    }

    /** Sends the step data to the service
     * @param name Name of step
     * @param data payload to send
     * @param authenticated flag to define if apply auth headers
     * @param loadDelay time in miliseconds to await before call /load resource to get the next step
     * @param skipErrorRedirect if the request will skip interceptor error response redirect action
     * @returns
     */
    public postStep<T>(name: string, data: any, authenticated: boolean = false, loadDelay: number = 300, skipErrorRedirect: boolean = false): Observable<T> {

        const loadPayload = { hashId: this.storage.getPropHashId() };

        data.hashId = loadPayload.hashId;

        let headers = new HttpHeaders();
        let params = this.setParamsDlb()

        if (skipErrorRedirect) {
            headers = this.appendSkipErrorRedirectHeaders(headers);
        }

        if (authenticated) {
            headers = this.appendHeaders(headers);
        }


        let request = authenticated ? this.https.post<T>(`${this.resource_url}/step/${name}`, data, { headers, params }) : this.https.post<T>(`${this.resource_url}/step/${name}`, data);

        return request.pipe(
            mergeMap((response) => {
                return this.postLoad<T>(loadPayload, response, loadDelay);
            }));
    }

    /**
     * Sends token for service validation
     * @param name step name
     * @param data data with token value
     * @param loadDelay time to delay LOAD service call
     * @param preLoadCallback function to be executed before call LOAD service
     * @returns
     */
    public postStepAuth<T>(name: string, data: any, loadDelay: number = 300, preLoadCallback?: Function): Observable<T> {
        const headers = this.appendHeaders();
        let identify;

        // this.monitoringService.sendRestDynatrace(name, data);
        return this.https.post<IIdentifyResponse>(urlConfig.url_api_identify, data, { headers })
            .pipe(
                switchMap((identifyResponse) => {
                    identify = identifyResponse
                    if (urlConfig.dlb_crypt === 'sim') {
                        return this.encryptionService.changeKeys();
                    }
                    return of('')
                }),
                mergeMap(() => {
                    let identifyResponse = identify;
                    this.storage.setPropHashId(identifyResponse.propHashId);
                    return preLoadCallback ? preLoadCallback(identifyResponse) : identifyResponse
                }),
                mergeMap((identifyResponse) => {
                    const gaId = this.getPropHashId(identifyResponse);
                    return this.postLoad<T>(gaId, identifyResponse, loadDelay)
                        .pipe(
                            catchError(err => this.postLoad<T>(gaId, identifyResponse, loadDelay)))
                }))
    }

    /**
     * Call the load to fetch nextStep user data
     * @param dataToSend data to send to /load request
     * @param dataReceived data received from currentStep POST method
     * @param loadDelay time in miliseconds to await before call /load resource to get the next step payload
     * @returns
     */
    public postLoad<T>(dataToSend: any, dataReceived: any, loadDelay: number = 300): Observable<T> {
        const error = this.storage.getError();
        const DENIED = 'SECURITY_PROPOSAL_DENIED'
        if (error && error.title) {
            if (error.title == DENIED) return of(null);
        }

        let headers = this.appendHeaders();
        let params = this.setParamsDlb();

        return of(dataReceived).pipe(delay(loadDelay), mergeMap(() => {
            return this.https.post<ProposalResponse>(`${this.resource_url}/step/${STEP_ID.LOAD}`, dataToSend, { headers, params }).pipe(
                map((value) => {
                    const proposal = {...value, ...dataReceived};

                    this.storage.updateProposal(proposal);

                    return proposal;
                }),
                catchError((err: ErrorResponse) => {
                    const error = { ...err.error, isLoadRequest: true }
                    this.pageService.goToError({ ...err, error })
                    return of(null);
                }))
        }))
    }

    get authHeaders() {
        return new HttpHeaders({
            'authorization': this.storage.getAuthKey(),
        });
    }

    appendHeaders(headers:HttpHeaders = new HttpHeaders()) {

        if(this.storage.getAuthKey() !== 'false'){
            headers = headers.append('authorization', this.storage.getAuthKey())
        }

        // if(this.storage.getSantanderHash() !== 'false'){
        //     headers = headers.append(HttpHeadersChannel.HASH, this.storage.getSantanderHash());
        // }
        return headers;
    }

    appendSkipErrorRedirectHeaders(headers: HttpHeaders) {
        return headers ? headers.append('x-error-redirect', 'skip') : new HttpHeaders({
            'x-error-redirect': 'skip',
        });
    }

    private getPropHashId(data) {
        return {
            'hashId': data['propHashId']
        }
    }

    public postTracking(trackingData) {
        return this.https.post(this.TRACKING_URL, trackingData, {
            headers: this.appendHeaders(new HttpHeaders())
        })
    }

    public validateCaptcha(payload, type: string = "invisible") {
        const data = {
            captchaToken: payload,
            captchaTokenVersion: type === "invisible" ? "v3" : "v2",
            systemCode: urlConfig.keys.captcha.sytemCode,
        };
        return this.https
            .post(`${urlConfig.base.urlHub}/${urlConfig.path_captcha}`, data, {
                params: new HttpParams().set(
                    "gw-app-key",
                    urlConfig.keys.captcha.captchaKey
                ),
            })
            .pipe(
                catchError((err) => {
                    return throwError({ ...err, type });
                })
            )
    }

    private setParamsDlb() {
        return {
            'x-apply-dlb': 'true'
        }
    }

    public postLogStep<T>(name: string, data: LogRequest) {
        const loadPayload = { hashId: this.storage.getPropHashId() };

        data.hashId = loadPayload.hashId;
        data.log = true;

        let headers = this.appendHeaders();

        headers = this.appendSkipErrorRedirectHeaders(headers);

        return this.https.post<T>(`${this.resource_url}/step/${name}`, data, {
            headers,
        });
    }
}
